import { MenuItem, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DiscoveryAnswer, DiscoveryQuestion } from '../../../../types/advise';
import { shownAnswer } from './DiscoveryPanel.calc';
import { DiscoveryNotesBox } from './DiscoveryNotesBox';
import { DiscoveryPickAny } from './DiscoveryPickAny';
import { DISCOVERY_NUMBER_LIMIT, DISCOVERY_TEXT_LIMIT } from './discoveryLimits';

const WHOLE_NUMBER = /^\d{1,10}$/;

export interface DiscoveryAnswerProps {
  question: DiscoveryQuestion;
  onAnswer: (value: DiscoveryAnswer) => void;
}

export const DiscoveryAnswerInput = ({ question, onAnswer }: DiscoveryAnswerProps) => {
  const value = question.value ?? {};
  const answer = shownAnswer(question);
  const setAnswer = (next: DiscoveryAnswer['answer']) => onAnswer({ ...value, answer: next });

  switch (question.field_type) {
    case 'text':
      return (
        <TextField
          fullWidth
          size="small"
          value={typeof answer === 'string' ? answer : ''}
          placeholder={question.placeholder ?? undefined}
          inputProps={{ 'aria-label': question.label, maxLength: DISCOVERY_TEXT_LIMIT }}
          onChange={(event) => setAnswer(event.target.value)}
        />
      );
    case 'notes':
      return (
        <DiscoveryNotesBox
          value={typeof answer === 'string' ? answer : ''}
          label={question.label}
          onChange={setAnswer}
        />
      );
    case 'number':
      return (
        <TextField
          size="small"
          value={typeof answer === 'number' ? String(answer) : ''}
          placeholder="0"
          fullWidth
          sx={{ maxWidth: 160 }}
          inputProps={{ 'aria-label': question.label, inputMode: 'numeric', style: { textAlign: 'right' } }}
          onChange={(event) => {
            const typed = event.target.value;
            if (typed === '') setAnswer(null);
            else if (WHOLE_NUMBER.test(typed) && Number(typed) <= DISCOVERY_NUMBER_LIMIT) setAnswer(Number(typed));
          }}
        />
      );
    case 'yes_no':
      return (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={typeof answer === 'boolean' ? answer : null}
          aria-label={question.label}
          onChange={(_, next: boolean | null) => {
            if (next !== null) setAnswer(next);
          }}>
          <ToggleButton
            value={true}
            sx={{ px: 2 }}>
            Yes
          </ToggleButton>
          <ToggleButton
            value={false}
            sx={{ px: 2 }}>
            No
          </ToggleButton>
        </ToggleButtonGroup>
      );
    case 'select':
      return (
        <TextField
          select
          fullWidth
          size="small"
          value={typeof answer === 'string' ? answer : ''}
          inputProps={{ 'aria-label': question.label }}
          onChange={(event) => setAnswer(event.target.value)}>
          {(question.choices ?? []).map((choice) => (
            <MenuItem
              key={choice}
              value={choice}>
              {choice}
            </MenuItem>
          ))}
        </TextField>
      );
    case 'multi_select':
      return (
        <DiscoveryPickAny
          question={question}
          onAnswer={onAnswer}
        />
      );
  }
};
